// @ts-strict-ignore
import {TRACKING_EVENTS as CustomPropertyModelTrackingEvent} from 'common/widgets/custom-property-modal/constants';
import {TRACKING_EVENTS as QueryBlockTrackingEvent} from 'common/query-builder/query-block/constants';
import {TrackingEvent as PropertiesMenuTrackingEvent} from 'common/query-builder/query-block/properties-menu/tracking';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {
  CardOnDashboard,
  ContentsResponse,
  ContentType,
  Dashboard,
  DashboardCard,
  MediaCard,
  TextCard,
} from 'common/report/dashboards/types';
import {getDashboardAllowedReports} from 'common/report/util/boards';
import {ContentActionType} from 'common/report/dashboards/types';
import {MediaCardType} from 'common/report/dashboards/types';
import {SidenavSectionTrackingName} from 'common/widgets/content-sidenav/constants';
import {isDashboardCard} from 'common/report/dashboards/util';
import {isExperimentPropertyName} from 'common/report/util/properties';

import {getUserAccess} from '../util/saved-entities/utils';

export enum TrackingEvent {
  // Dashboard actions
  DashboardLoaded = `Dashboard loaded`,
  DashboardFirstFetchIssued = `Dashboard First Fetch Issued`,
  DashboardInitialCardsFetched = `Dashboard Initial Cards Fetched`,
  DashboardAllCardsFetched = `Dashboard All Cards Fetched`,
  DashboardSidenavLoaded = `Dashboard sidenav loaded`,
  DashboardCardRepolled = `Dashboard card repolled`,
  ClickDuplicateDashboard = `Clicked duplicate dashboard`,
  ClickedDeleteDashboard = `Clicked delete dashboard`,
  ClickedTVModeLink = `Clicked TV Mode Link`,
  CreateDashboard = `Create dashboard`,
  DeleteDashboard = `Create new dashboard`, // misnamed but is reformatted in lexicon
  MoveDashboard = `Move Dashboard`,
  DuplicateDashboard = `Duplicate Dashboard`,
  EditDashboardDescription = `Edit dashboard description`,
  EditDashboardTitle = `Edit dashboard title`,
  OpenDashboardOverflowMenu = `Open dashboard overflow menu`,
  ReloadReport = `Reload report`,
  ViewDashboardsReport = `Viewed dashboards report`,
  EditDashboardSettings = `Edited Dashboard Settings`,
  SaveChanges = `Save changes`,
  OpenEntityModal = `Opened entity modal`,
  DashboardNotSaved = `Dashboard Not Saved`,
  DashboardSidenavOverflowMenuActionSelected = `Dashboard Sidenav Overflow Menu Action Selected`,

  // adding a card via the Add menu
  AddCard = `Add card`,

  // Dashboard selector
  OpenDashboardSelector = `Opened dashboard selector`,
  SelectDashboard = `Select Dashboard`,
  UpdateSelectedDashboard = `Updated selected dashboard`,

  // Date filters
  ResetDateFilter = `Reset date filter`,
  SelectDateRange = `Select date range`,

  // Global filter bar
  ClickedAddFilter = `Clicked add filter`,
  ClickedRemoveFilter = `Clicked remove filter`,
  ClickedEditFilter = `Clicked to edit filter`,

  // Row actions
  CopyRowUrl = `Copy Row URL`,
  DuplicateRow = `Duplicate Row`,
  MoveRowToTopOrBottom = `Move Row to Top or Bottom`,
  DragRow = `Drag Row`,
  CombineRows = `Combine Rows`,

  // Card actions
  ClickedReportLink = `Bookmark loaded`, // clicking on a link to the report data. misnamed but is reformatted in lexicon
  CloseReport = `Close Report`,
  ChangeVisualization = `Change visualization`,
  CopyReportUrl = `Copy Report URL`,
  OpenChartTypeMenu = `Opened change type visualization menu`,
  OpenOverflowMenu = `Opened overflow menu`,
  DuplicateCard = `Duplicated card`,
  DeleteCard = `Delete card`,
  UpdateCard = `Update card`,
  UpdateReportName = `Updated report name`,
  PseudoChartRendered = `Pseudo Chart Rendered`,
  SetUpButtonClick = `Empty Report Setup Button Clicked`,
  MoveCardToTopOrBottom = `Move Card to Top or Bottom`,

  // Text card actions
  EditTextCard = `Edit text card`,
  FormatTextCard = `Format text card`,

  // Media card actions
  MediaCardHover = `Hover media card`,
  UploadImageDoubleClick = `Upload image double click`,
  EmbeddedVideoClick = `Embedded video click`,
  FullSceenOverflowMenu = `Full screen from overflow selected`,

  // Card type queries
  ReportQuery = `Report query`,

  // Onboarding dashboards
  RedirectedToOnboardingDashboard = `Redirected to onboarding dashboard`,
  RedirectedToMyDashboard = `Redirected to "My Dashboard"`,

  // Favoriting
  Favorited = `Favorited Dashboard`,
  Unfavorited = `Unfavorited Dashboard`,

  // Pinning
  Pinned = `Pinned Dashboard`,
  Unpinned = `Unpinned Dashboard`,

  CopySidenavLink = `Copy Sidenav Link`,

  // Kpi Templates
  CreatedDashboardFromTemplate = `Created Dashboard From Template`,

  // Foundational Templates
  OpenedTemplateSelector = `[Foundational Templates] Opened Template Selector`,
  ViewedEventSelector = `[Foundational Templates] Viewed Event Selector`,
  CreateDashboardFromTemplate = `[Foundational Templates] Created Dashboard`,
  ClickPreviewTemplate = `[Foundational Templates] Click View Preview`,

  // Grid Layout
  AddCardClicked = `Add card clicked`,
  MoveCard = `Move card`,
  ResizeRow = `Resize Row`,
  CardChanged = `Card Changed`,
  LayoutValidationException = `Layout validation exception`,
  ReorderDashboardCard = `Reorder dashboard`,
  ResizeDashboardCard = `Resize dashboard card`,

  // RHS
  CommitBlueprint = `[RHS] Save Dashboard from Blueprint`,
  UpdateBlueprint = `[RHS] Update Blueprint`,
  DiscardBlueprint = `[RHS] Discard Blueprint`,
  ViewedDiscardPrompt = `[RHS] Viewed Discard Prompt`,
  WentBackToAllTemplates = `[RHS] Went Back to All Templates`,

  // AI Summary
  AISummaryClicked = `[AI Summary] AI Summary Button Clicked`,
  AISummaryGenerated = `[AI Summary] AI Summary Generated`,
}

export enum SprigTrackingEvent {
  AbandonedTemplateFlow = `AbandonedTemplateFlow`,
  SavedTemplateFlow = `SavedTemplateFlow`,
  SavedReport = `SavedReport`,
}

export const CONTENT_ACTION_TRACKING_NAME_MAP = {
  [ContentActionType.Create]: null, // we track this through the ContentAdder component
  [ContentActionType.Delete]: TrackingEvent.DeleteCard,
  [ContentActionType.Duplicate]: TrackingEvent.DuplicateCard,
  [ContentActionType.Move]: TrackingEvent.MoveCard,
  [ContentActionType.Update]: TrackingEvent.UpdateCard,
};
// These props are not used as event properties directly, instead, they're used to calculate other properties
export enum GridCalcTrackingPropsParams {
  Contents = `Contents`,
  SingleContent = `Single Content`,
}

// Track event property keys
const STARTING_ROW = `Starting Row`;
const ENDING_ROW = `Ending Row`;

export const ProductionTrackingEvents = new Set<string>([
  ...Object.values(TrackingEvent),
  ...Object.values(QueryBlockTrackingEvent),
  ...Object.values(PropertiesMenuTrackingEvent),
  ...Object.values(CustomPropertyModelTrackingEvent),
]);

// Track where CRUD operation is initiated
export enum DashboardCRUDSource {
  Header = `header`,
  Sidenav = `sidenav`,
  RHS = `rhs`,
}

export enum DashboardMoveSource {
  Header = `header`,
  SidenavDragAndDrop = `sidenav-drag-and-drop`,
  SidenavOverflowMenu = `sidenav-overflow-menu`,
  // Card -> tracked as a regular card update through `[Web Dash] Save changes`
}

//TODO: With grid and some of the dashboard layout changes, the card param within the dashboard object
// which is often passed to this function is stale. Instead of relying on that,
// we are instead temporarily just passing the cards into this function which is a more
// reliable param. Once we have made the updates necessary so that the dashboard object's card
// param is reliable, we should update this function so it no longer accepts cards and instead uses dashboard.cards
// https://www.notion.so/mxpnl/Clear-update-the-cache-when-deleting-updating-or-creating-report-97436b80bccf4d01872071d8a0128956
export function getCommonDashboardTrackingProps({
  dashboard,
  cards,
  sourceTrackingProps,
}: {
  dashboard: Partial<Dashboard>;
  cards?: CardOnDashboard[];
  sourceTrackingProps?: {crudSource?: DashboardCRUDSource; sidenavSectionSource?: SidenavSectionTrackingName};
}): Obj {
  return {
    'dashboard id': dashboard?.id,
    description: dashboard?.description,
    'description - length': dashboard?.description?.length,
    title: dashboard?.title,
    'title - length': dashboard?.title?.length,
    'action source': sourceTrackingProps?.crudSource,
    'sidenav section source': sourceTrackingProps?.sidenavSectionSource,
    'permission level': dashboard
      ? getUserAccess(dashboard, {
          ownerId: dashboard.creator_id,
          userId: userCookieUtils.getUserIdentityFromCookie()?.id,
        })
      : null,
    ...getCardTypes(cards),
    'num cards': cards?.length,
    'Request-URL': window.location.href,
    'text card styles': getTextCardStyles(cards),
    'num experiment filters': dashboard?.filters?.filter((filter) => isExperimentPropertyName(filter.propertyName))
      .length,
    'num filters': dashboard?.filters?.length,
    'num experiment breakdowns': dashboard?.breakdowns?.filter((breakdown) =>
      isExperimentPropertyName(breakdown.propertyName),
    ).length,
    'num breakdowns': dashboard?.breakdowns?.length,
    breakdown_types: dashboard?.breakdowns?.map((breakdown) => breakdown.resourceType),
    timestamp_ms: Date.now(),
  };
}

enum TextCardStyles {
  H2 = `<h2>`,
  H3 = `<h3>`,
  Bold = `<strong>`,
  Italics = `<em>`,
  Strike = `<s>`,
  Quote = `<blockquote>`,
  BulletList = `<ul>`,
  OrderedList = `<ol>`,
  Underline = `<u>`,
  Highlight = `<mark>`,
  Link = `href`,
  Divider = `<hr>`,
}

export function getPublicDashboardContentStats(contents: ContentsResponse): {
  numHyperlinks: number;
  numContent: number;
} {
  if (!contents) {
    return {numHyperlinks: 0, numContent: 0};
  }

  let numHyperlinks = 0;
  for (const tc of Object.values(contents[ContentType.Text] ?? {})) {
    numHyperlinks += tc.markdown?.match(/href="([^"]*)"/g)?.length ?? 0;
  }

  let numContent = 0;
  for (const value of Object.values(contents)) {
    numContent += Object.keys(value ?? {}).length;
  }

  return {numHyperlinks, numContent};
}

export function getTextCardStyles(cards: CardOnDashboard[]): Obj {
  const styles = {};
  Object.keys(TextCardStyles).forEach((style) => (styles[style] = false));
  cards?.forEach((card) => {
    if (card.type === ContentType.Text) {
      const textCard = card as TextCard;
      Object.keys(TextCardStyles).forEach((style) => {
        const containsStyle = textCard.markdown?.includes(TextCardStyles[style]);
        if (containsStyle) {
          styles[style] = containsStyle;
        }
      });
    }
  });
  return styles;
}

export function getCardTypes(cards?: CardOnDashboard[]): Obj {
  const cardTypeCounts = getZeroCardTypeCounts();
  cards?.forEach((card) => {
    const cardTypeKey = getCardTypeTrackingKey(card);
    // Keys outside the seeded set (e.g. a report whose bookmark type is not board-allowed) still need a
    // numeric count rather than NaN.
    cardTypeCounts[cardTypeKey] = (cardTypeCounts[cardTypeKey] ?? 0) + 1;
  });
  return cardTypeCounts;
}

function getCardTypeTrackingKey(card: CardOnDashboard): string {
  if (isDashboardCard(card)) {
    const dashboardCard = card as DashboardCard;
    return `num ${dashboardCard.bookmark?.type} ${card.type}s`;
  }
  if (card.type === ContentType.Media) {
    return `num ${(card as MediaCard).media_type} cards`;
  }
  return `num ${card.type} cards`;
}

// Report and report-link cards are keyed by their bookmark's report type and media cards by their media type, so
// those content types are seeded through their sub-types. Row is a content-action target, never a card on the board.
const CONTENT_TYPES_WITHOUT_PLAIN_CARD_COUNT = new Set<ContentType>([
  ContentType.Report,
  ContentType.ReportLink,
  ContentType.Media,
  ContentType.Row,
]);

function getZeroCardTypeCounts(): Record<string, number> {
  const cardTypeCounts: Record<string, number> = {};
  getDashboardAllowedReports(null).forEach((reportType) => {
    cardTypeCounts[`num ${reportType} ${ContentType.Report}s`] = 0;
    cardTypeCounts[`num ${reportType} ${ContentType.ReportLink}s`] = 0;
  });
  Object.values(MediaCardType).forEach((mediaType) => {
    cardTypeCounts[`num ${mediaType} cards`] = 0;
  });
  Object.values(ContentType)
    .filter((contentType) => !CONTENT_TYPES_WITHOUT_PLAIN_CARD_COUNT.has(contentType))
    .forEach((contentType) => {
      cardTypeCounts[`num ${contentType} cards`] = 0;
    });
  return cardTypeCounts;
}

// "trackEvent" from grid-row
export type GridRowTrackEventDetail = ResizeCardTrackEventDetail | ResizeRowTrackEventDetail;

export type ResizeCardTrackEventDetail = {
  eventName: TrackingEvent.ResizeDashboardCard;
  trackProps: {
    'Original Size': number;
    'New Size': number;
    Delta: number;
    'Content Type': ContentType;
  };
  calcTrackPropsParams: {
    [GridCalcTrackingPropsParams.SingleContent]: {
      contentId: number | string;
      contentType: ContentType;
    };
  };
};

export type ResizeRowTrackEventDetail = {
  eventName: TrackingEvent.ResizeRow;
  trackProps: {
    'Starting height': number;
    'Ending height': number;
  };
  calcTrackPropsParams: {
    [GridCalcTrackingPropsParams.Contents]: Array<{
      contentId: number | string;
      contentType: ContentType;
    }>;
  };
};

export type DuplicateRowTrackEventDetail = {
  eventName: TrackingEvent.DuplicateRow;
  trackProps: {
    'Num Cells': number;
    'Row Index': number;
  };
  calcTrackPropsParams: {
    [GridCalcTrackingPropsParams.Contents]: Array<{
      contentId: number | string;
      contentType: ContentType;
    }>;
  };
};

// "trackEvent" from grid-layout
export type GridLayoutTrackEventDetail =
  | AddCardTrackEventDetail
  | ReorderCardTrackEventDetail
  | AddCardClickTrackEventDetail
  | DuplicateRowTrackEventDetail
  | GridRowTrackEventDetail
  | MoveCardTrackEventDetail
  | MoveRowTrackEventDetail
  | DragRowTrackEventDetail
  | CombineRowsTrackEventDetail;

export type AddCardTrackEventDetail = {
  eventName: TrackingEvent.AddCard;
  trackProps: {
    Location: TODO;
    'Card Type': TODO;
    Direction?: string;
  };
  calcTrackPropsParams?: null;
};

export type ReorderCardTrackEventDetail = {
  eventName: TrackingEvent.ReorderDashboardCard;
  trackProps: {
    [STARTING_ROW]: number;
    [ENDING_ROW]: number;
    'Starting Index': number;
    'Ending Index': number;
    'Is New Row': boolean;
    'Content Type': ContentType;
  };
  calcTrackPropsParams?: null;
};

export type MoveCardTrackEventDetail = {
  eventName: TrackingEvent.MoveCardToTopOrBottom;
  trackProps: {
    [STARTING_ROW]: number;
    [ENDING_ROW]: number;
    Destination: `top` | `bottom`;
  };
  calcTrackPropsParams?: null;
};

export type MoveRowTrackEventDetail = {
  eventName: TrackingEvent.MoveRowToTopOrBottom;
  trackProps: {
    [STARTING_ROW]: number;
    [ENDING_ROW]: number;
    Destination: `top` | `bottom`;
  };
  calcTrackPropsParams?: null;
};

export type DragRowTrackEventDetail = {
  eventName: TrackingEvent.DragRow;
  trackProps: {
    [STARTING_ROW]: number;
    [ENDING_ROW]?: number;
  };
  calcTrackPropsParams?: null;
};

export type CombineRowsTrackEventDetail = {
  eventName: TrackingEvent.CombineRows;
  trackProps: {
    'Source Row': number;
    'Target Row': number;
    'Num Cells Moved': number;
    'Final Num Cells': number;
  };
  calcTrackPropsParams?: null;
};

export type AddCardClickTrackEventDetail = {
  eventName: TrackingEvent.AddCardClicked;
  trackProps: {
    Location: `Row` | `Bottom`;
  };
  calcTrackPropsParams?: null;
};
